import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';

function EditMembershipPlan() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    status: 'active',
    benefits: ['']
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchMembershipPlan();
  }, [id]);
  
  const fetchMembershipPlan = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getMembershipPlan(id);
      
      // Ensure benefits is an array
      const benefits = Array.isArray(data.benefits) && data.benefits.length > 0 
        ? data.benefits 
        : [''];
      
      setFormData({
        ...data,
        benefits
      });
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching membership plan:', error);
      setError('Failed to load membership plan');
      setIsLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const handleBenefitChange = (index, value) => {
    const updatedBenefits = [...formData.benefits];
    updatedBenefits[index] = value;
    
    setFormData({
      ...formData,
      benefits: updatedBenefits
    });
  };
  
  const addBenefit = () => {
    setFormData({
      ...formData,
      benefits: [...formData.benefits, '']
    });
  };
  
  const removeBenefit = (index) => {
    if (formData.benefits.length > 1) {
      const updatedBenefits = formData.benefits.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        benefits: updatedBenefits
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Plan name is required';
    if (!formData.description) newErrors.description = 'Description is required';
    
    if (!formData.price) {
      newErrors.price = 'Price is required';
    } else if (isNaN(formData.price) || formData.price < 0) {
      newErrors.price = 'Price must be a valid positive number';
    }
    
    // Check for empty benefits
    const emptyBenefits = formData.benefits.some(benefit => !benefit);
    if (emptyBenefits) {
      newErrors.benefits = 'All benefits must have a value';
    }
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit to API - remove duration from submission
      await adminService.updateMembershipPlan(id, {
        ...formData,
        price: parseFloat(formData.price)
      });
      
      // Redirect to membership plan details page after successful submission
      navigate(`/admin/membership-plans/${id}`);
    } catch (error) {
      console.error('Error updating membership plan:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="admin-error">
          Error: {error}
          <button 
            className="admin-btn admin-btn-primary" 
            onClick={() => navigate('/admin/membership-plans')}
            style={{ marginTop: '1rem' }}
          >
            Back to Membership Plans
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Edit Membership Plan</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/membership-plans">Membership Plans</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to={`/admin/membership-plans/${id}`}>{formData.name}</Link>
          </li>
          <li className="admin-breadcrumb-item active">Edit</li>
        </ul>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Edit Plan Information</h2>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 2 }}>
                <label htmlFor="name" className="admin-form-label">Plan Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.name ? 'admin-input-error' : ''}`}
                />
                {errors.name && <div className="admin-error-message">{errors.name}</div>}
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="status" className="admin-form-label">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="admin-form-control"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="description" className="admin-form-label">Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.description ? 'admin-input-error' : ''}`}
                  rows="3"
                ></textarea>
                {errors.description && <div className="admin-error-message">{errors.description}</div>}
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label htmlFor="price" className="admin-form-label">Price (₹) *</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.price ? 'admin-input-error' : ''}`}
                  min="0"
                  step="1"
                />
                {errors.price && <div className="admin-error-message">{errors.price}</div>}
              </div>
            </div>
            
            <div className="admin-form-section" style={{ marginTop: '1.5rem' }}>
              <h3 className="admin-section-title" style={{ marginBottom: '1rem' }}>Membership Benefits</h3>
              
              {errors.benefits && (
                <div className="admin-error-message" style={{ marginBottom: '1rem' }}>{errors.benefits}</div>
              )}
              
              {formData.benefits.map((benefit, index) => (
                <div key={index} className="admin-form-row" style={{ alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div className="admin-form-group" style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={benefit}
                      onChange={(e) => handleBenefitChange(index, e.target.value)}
                      className="admin-form-control"
                      placeholder={`Benefit ${index + 1}`}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => removeBenefit(index)}
                      className="admin-btn admin-btn-danger"
                      disabled={formData.benefits.length === 1}
                      style={{ padding: '0.5rem' }}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                    {index === formData.benefits.length - 1 && (
                      <button
                        type="button"
                        onClick={addBenefit}
                        className="admin-btn admin-btn-primary"
                        style={{ padding: '0.5rem' }}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="admin-form-actions" style={{ marginTop: '2rem' }}>
              <Link
                to={`/admin/membership-plans/${id}`}
                className="admin-btn admin-btn-light"
              >
                Cancel
              </Link>
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Update Plan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

export default EditMembershipPlan;
